import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { NestedQuestion, QuestionType, TaxResponse, TaxResponseGroup } from '@/tax-detail/models';

type StateRecord = {
    questionId: number;
    groupUid: string;
    groupId?: number;
    value: any;
};

@Injectable({ providedIn: 'root' })
export class QuestionStateService {
    constructor() {}

    private exportableState = new Subject<{ fieldId: number; values: StateRecord[] }>();
    stateObservable = this.exportableState.asObservable();
    private internalState = {} as Record<number, StateRecord[]>;
    private externalState = {} as Record<number, StateRecord[]>;

    private exportState(fieldId: number) {
        this.exportableState.next({ fieldId: fieldId, values: this.getCurrentFieldState(fieldId) });
    }

    clearState(): void {
        this.internalState = {};
        this.externalState = {};
    }

    clearQuestionGroupState(questionId: number, groupUid: string) {
        let affectedKeys = [] as number[];
        Object.keys(this.internalState)
            .map((key) => Number(key))
            .forEach((fieldId) => {
                const currentVals = this.internalState[fieldId];
                const newVals = currentVals.filter((stateRec) => stateRec.questionId !== questionId || stateRec.groupUid !== groupUid);
                if (newVals.length !== currentVals.length) {
                    affectedKeys.push(fieldId);
                    this.internalState[fieldId] = newVals;
                }
            });
        affectedKeys.forEach((fieldId) => this.exportState(fieldId));
    }

    getCurrentFieldValue(fieldId: number): any[] {
        return (this.internalState[fieldId] ?? []).map((stateRec) => stateRec.value);
    }

    getCurrentFieldState(fieldId: number): StateRecord[] {
        return [...(this.internalState[fieldId] ?? [])];
    }

    getCurrentFieldValueExternal(fieldId: number): any[] {
        return (this.externalState[fieldId] ?? []).map((stateRec) => stateRec.value);
    }

    getCurrentFieldStateExternal(fieldId: number): StateRecord[] {
        return [...(this.externalState[fieldId] ?? [])];
    }

    addToExternalState(question: NestedQuestion, response: TaxResponse) {
        response.groups.forEach((group) => this.addGroupToExternalState(question, group));
    }

    addFieldToExternalState(questionId: number, groupId: number, fieldId: number, value: boolean) {
        let currentFieldState = this.externalState[fieldId] ?? [];
        currentFieldState.push({ groupUid: '', groupId, questionId, value });
        this.externalState[fieldId] = currentFieldState;
    }

    resolveExternalGroupId(question: NestedQuestion, groupUid: string, groupId: number) {
        question.inputFields?.forEach((field) =>
            (this.externalState[field.id] ?? [])
                .filter((stateRec) => stateRec.questionId === question.id && stateRec.groupId === groupId)
                .forEach((stateRec) => (stateRec.groupUid = groupUid)),
        );
    }

    private addGroupToExternalState(question: NestedQuestion, group: TaxResponseGroup) {
        group.records.forEach((rec) => {
            let field = question.inputFields?.find((field) => field.id == rec.inputField);
            if (rec.nestedGroups !== undefined) {
                rec.nestedGroups.forEach((nestedGroup) => this.addGroupToExternalState(field?.nestedQuestion!, nestedGroup));
            } else {
                let current = this.externalState[rec.inputField] ?? [];
                const val = field?.type === QuestionType.SelectDependent ? rec.dependsOnGroup : rec.data;
                current.push({ groupUid: '', groupId: group.id, questionId: question.id, value: val });
                this.externalState[rec.inputField] = current;
            }
        });
    }

    signalFieldUpdate(questionId: number, groupUid: string, fieldId: number, value: any, groupId?: number) {
        let currentFieldState = (this.internalState[fieldId] ?? []).filter(
            (stateVec) => stateVec.questionId !== questionId || stateVec.groupUid !== groupUid,
        );
        currentFieldState.push({ questionId: questionId, groupUid: groupUid, groupId: groupId, value: value });
        this.internalState[fieldId] = currentFieldState;
        this.exportState(fieldId);
    }
}
